import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Download } from 'lucide-react';

// Import archetype images
import thinkerImg from '@/assets/archetype-thinker.png';
import escapistImg from '@/assets/archetype-escapist.png';
import dreamerImg from '@/assets/archetype-dreamer.png';
import connectorImg from '@/assets/archetype-connector.png';
import archivistImg from '@/assets/archetype-archivist.png';
import explorerImg from '@/assets/archetype-explorer.png';

interface ArchetypeDef { name: string; src: string; filename: string }
const archetypeImages: ArchetypeDef[] = [
  { name: 'Explorer', src: explorerImg, filename: 'archetype-explorer.png' },
  { name: 'Thinker', src: thinkerImg, filename: 'archetype-thinker.png' },
  { name: 'Dreamer', src: dreamerImg, filename: 'archetype-dreamer.png' },
  { name: 'Connector', src: connectorImg, filename: 'archetype-connector.png' },
  { name: 'Escapist', src: escapistImg, filename: 'archetype-escapist.png' },
  { name: 'Archivist', src: archivistImg, filename: 'archetype-archivist.png' },
];

// --- Deterministic checkerboard stripper (no ML) ---
function colorDistance(c1: number[], c2: number[]) {
  const dr = c1[0] - c2[0];
  const dg = c1[1] - c2[1];
  const db = c1[2] - c2[2];
  return Math.sqrt(dr*dr + dg*dg + db*db);
}

function sampleBorderColors(imageData: ImageData) {
  const colors: number[][] = [];
  const { width, height, data } = imageData;
  const step = Math.max(1, Math.floor(Math.min(width, height) / 64));
  for (let x = 0; x < width; x += step) {
    const top = (x * 4);
    const bottom = ((height - 1) * width + x) * 4;
    colors.push([data[top], data[top+1], data[top+2]]);
    colors.push([data[bottom], data[bottom+1], data[bottom+2]]);
  }
  for (let y = 0; y < height; y += step) {
    const left = (y * width) * 4;
    const right = (y * width + (width - 1)) * 4;
    colors.push([data[left], data[left+1], data[left+2]]);
    colors.push([data[right], data[right+1], data[right+2]]);
  }
  return colors;
}

function kmeans2(colors: number[][], iters = 6) {
  // Init centroids with two farthest samples
  let c1 = colors[0];
  let c2 = colors.reduce((prev, cur) => (colorDistance(cur, c1) > colorDistance(prev, c1) ? cur : prev), colors[0]);
  for (let i = 0; i < iters; i++) {
    const g1: number[][] = [], g2: number[][] = [];
    for (const c of colors) (colorDistance(c, c1) < colorDistance(c, c2) ? g1 : g2).push(c);
    const mean = (g: number[][]) => g.length ? [0,1,2].map(k => Math.round(g.reduce((s,v)=>s+v[k],0)/g.length)) : [0,0,0];
    c1 = mean(g1); c2 = mean(g2);
  }
  // Ensure c1 is the darker tile (helps thresholding)
  const l1 = 0.2126*c1[0] + 0.7152*c1[1] + 0.0722*c1[2];
  const l2 = 0.2126*c2[0] + 0.7152*c2[1] + 0.0722*c2[2];
  return l1 <= l2 ? [c1, c2] as const : [c2, c1] as const;
}

function stripCheckerboard(img: HTMLImageElement, threshold = 28, feather = 1): Promise<Blob> {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  ctx.drawImage(img, 0, 0);

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  // Get two dominant border colors (checker tiles)
  const borderColors = sampleBorderColors(imageData);
  const [tileA, tileB] = kmeans2(borderColors);

  // Make pixels close to either tile transparent
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i+1], b = data[i+2];
    const dA = colorDistance([r,g,b], tileA);
    const dB = colorDistance([r,g,b], tileB);
    if (Math.min(dA, dB) < threshold) {
      data[i+3] = 0;
    }
  }

  // Optional feather (1px box blur on alpha)
  if (feather > 0) {
    const w = imageData.width, h = imageData.height;
    const alpha = new Uint8ClampedArray(w * h);
    for (let i = 0, p = 0; i < data.length; i += 4, p++) alpha[p] = data[i+3];
    const out = new Uint8ClampedArray(alpha);
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        let sum = 0, count = 0;
        for (let dy = -feather; dy <= feather; dy++) {
          for (let dx = -feather; dx <= feather; dx++) {
            const nx = x + dx, ny = y + dy;
            if (nx>=0 && nx<w && ny>=0 && ny<h) { sum += alpha[ny*w + nx]; count++; }
          }
        }
        out[y*w + x] = Math.round(sum / count);
      }
    }
    for (let p = 0, i = 0; p < out.length; p++, i += 4) data[i+3] = out[p];
  }

  ctx.putImageData(imageData, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/png', 1.0);
  });
}

async function loadImageFromSrc(src: string): Promise<HTMLImageElement> {
  const response = await fetch(src);
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });
}

export default function FixMascots() {
  const [processing, setProcessing] = useState<string | null>(null);
  const [processedImages, setProcessedImages] = useState<Record<string, string>>({});
  const { toast } = useToast();

  // Basic SEO
  document.title = 'Fix Mascots – Checkerboard Remover';
  const meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    const m = document.createElement('meta');
    m.name = 'description';
    m.content = 'Remove checkerboard backgrounds from mascot PNGs deterministically in-browser.';
    document.head.appendChild(m);
  }

  const processImage = async (name: string, src: string, filename: string) => {
    setProcessing(name);
    try {
      const img = await loadImageFromSrc(src);
      const cleaned = await stripCheckerboard(img);
      const url = URL.createObjectURL(cleaned);
      setProcessedImages(prev => ({ ...prev, [name]: url }));

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();

      toast({ title: 'Success', description: `${name} cleaned. File downloaded.` });
    } catch (e: any) {
      console.error(e);
      toast({ title: 'Error', description: e?.message ?? 'Failed to process image', variant: 'destructive' });
    } finally {
      setProcessing(null);
    }
  };

  const processAll = async () => {
    toast({ title: 'Processing started', description: 'Cleaning all mascots…' });
    for (const { name, src, filename } of archetypeImages) {
      await processImage(name, src, filename);
    }
    toast({ title: 'All done', description: 'All mascots cleaned and downloaded.' });
  };

  return (
    <div className="container mx-auto p-8 space-y-6">
      <h1 className="text-3xl font-heading font-bold text-foreground">Checkerboard Remover</h1>
      <p className="text-muted-foreground">Deterministic, no-ML cleaner for mascot PNGs. Downloads lossless PNGs you can replace in src/assets.</p>

      <Card className="p-6">
        <div className="flex items-center gap-3">
          <Button onClick={processAll} disabled={processing !== null}>
            {processing ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin"/>Processing {processing}…</>) : (<><Download className="mr-2 h-4 w-4"/>Process All</>)}
          </Button>
          <span className="text-sm text-muted-foreground">Files will download automatically.</span>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
        {archetypeImages.map(({ name, src, filename }) => (
          <Card key={name} className="p-4">
            <h3 className="font-semibold mb-2">{name}</h3>
            <div className="space-y-2">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Before</p>
                <div className="bg-muted rounded-lg p-4">
                  <img src={src} alt={`${name} original mascot`} className="w-full h-auto" />
                </div>
              </div>
              {processedImages[name] && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">After</p>
                  <div className="bg-gradient-to-br from-secondary/20 to-primary/20 rounded-lg p-4">
                    <img src={processedImages[name]} alt={`${name} cleaned mascot`} className="w-full h-auto" />
                  </div>
                </div>
              )}
            </div>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => processImage(name, src, filename)}
              disabled={processing !== null}
              className="w-full mt-3"
            >
              {processing === name ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin"/>Processing…</>) : (<><Download className="mr-2 h-4 w-4"/>Process</>)}
            </Button>
          </Card>
        ))}
      </div>
    </div>
  );
}
